/**
 * SEC-12 — a coarse, client-computed device signal sent voluntarily with a
 * signup request. NOT a device identity: WebAuthn exposes none (every
 * registration mints an unrelated credential; `aaguid` is the authenticator
 * MODEL, shared by millions). This is a FLAG for admin review when the same
 * signal repeats across signups, never an enforcement gate — see
 * ~/.claude/plans/signup-abuse-hardening.md (SEC-12).
 *
 * Deliberately coarse (UA + platform + language + screen + timezone): enough
 * entropy to notice "the same browser session tried this 5 times", not enough
 * to track an individual across sessions. Never blocks — a shared laptop
 * (the co-client couple this platform was built for) legitimately produces
 * repeats.
 */

async function sha256Hex(input: string): Promise<string> {
  const bytes = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export async function computeDeviceSignal(): Promise<string | undefined> {
  try {
    const parts = [
      navigator.userAgent,
      navigator.platform,
      navigator.language,
      `${screen.width}x${screen.height}`,
      Intl.DateTimeFormat().resolvedOptions().timeZone,
    ].join("|");
    return await sha256Hex(parts);
  } catch {
    // No Web Crypto (very old browser) or a sandboxed context — this is a
    // voluntary signal, so degrade to "none sent" rather than failing signup.
    return undefined;
  }
}
